import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../api'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setError('')
    setLoading(true)

    try {
      await api.post('/auth/reset-password', { token, password })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--cream)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        <div
          onClick={() => navigate('/')}
          style={{
            fontFamily: 'var(--font-display)', fontSize: '28px',
            fontWeight: 700, color: 'var(--navy)',
            textAlign: 'center', marginBottom: '40px', cursor: 'pointer',
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>

        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius-lg)',
          padding: '40px', boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--cream-dark)',
        }}>

          {success ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '56px', marginBottom: '20px' }}>✅</div>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: '22px',
                color: 'var(--navy)', marginBottom: '8px',
              }}>
                Password reset!
              </h2>
              <p style={{
                fontSize: '14px', color: 'var(--muted)', lineHeight: 1.7,
              }}>
                Your password has been updated. Redirecting you to login...
              </p>
            </div>
          ) : (
            <>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontSize: '24px',
                marginBottom: '8px', color: 'var(--navy)',
              }}>
                Set a new password
              </h2>
              <p style={{
                fontSize: '14px', color: 'var(--muted)', marginBottom: '32px',
              }}>
                Choose a strong password for your Velumio account.
              </p>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
                  }}>
                    New password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    style={{
                      width: '100%', padding: '12px 14px', fontSize: '14px',
                      border: '1.5px solid var(--warm-border)',
                      borderRadius: 'var(--radius-md)', outline: 'none',
                      fontFamily: 'var(--font-body)', color: 'var(--ink)',
                      background: 'var(--cream)', boxSizing: 'border-box' as const,
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
                  }}>
                    Confirm new password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your new password"
                    required
                    style={{
                      width: '100%', padding: '12px 14px', fontSize: '14px',
                      border: '1.5px solid var(--warm-border)',
                      borderRadius: 'var(--radius-md)', outline: 'none',
                      fontFamily: 'var(--font-body)', color: 'var(--ink)',
                      background: 'var(--cream)', boxSizing: 'border-box' as const,
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
                  />
                </div>

                {error && (
                  <div style={{
                    padding: '12px 14px', background: 'var(--rose-light)',
                    borderRadius: 'var(--radius-md)', fontSize: '13px',
                    color: 'var(--rose)', marginBottom: '20px', lineHeight: 1.5,
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  style={{
                    width: '100%', padding: '14px', fontSize: '16px',
                    fontWeight: 600, color: 'var(--white)',
                    background: loading || !password || !confirmPassword
                      ? 'var(--muted)' : 'var(--navy)',
                    border: 'none', borderRadius: 'var(--radius-md)',
                    cursor: loading || !password || !confirmPassword
                      ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}